import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart, StatCard, DataTable, COLORS } from '../charts';
import { runModel, parseInputs, encodeInputs, INPUT_SPEC, DEFAULTS, USAGE_RAMP, usd } from '../../data/dealModel';
import './portfolio.css';

// The portfolio deal model as a figure: inputs on the left, one of four panes
// on the right (the deal · revenue · margin · the firm's side). Inputs live in
// the URL (?portcos=45&adoption3=45) so a NoVA tour step — or a link in a
// deck — carries a scenario; only keys that differ from the defaults are
// written. `frame` is the controlled pane index from ?fig=deal-model&frame=.
const FRAMES = [
  { id: 'deal', label: 'The deal', note: 'Three years at the inputs on the left. Change any number; the URL carries the scenario.' },
  { id: 'revenue', label: 'Revenue', note: 'Rate-card prices. Subscription lines take the commit discount; usage lines never do.' },
  { id: 'margin', label: 'Margin', note: 'Cost of revenue by line. actual = a catalog price we pay · estimated = a planning figure.' },
  { id: 'savings', label: 'The firm’s side', note: 'The firm’s own assumptions: software replaced and builds avoided, against what it pays us.' },
];
const GROUPS = [...new Set(INPUT_SPEC.map((s) => s.group))];
const pctText = (v) => `${Math.round(v * 100)}%`;
const Basis = ({ b }) => <span className={`pf-basis is-${b.replace(/\s+/g, '-')}`}>{b}</span>;

export default function DealModel({ frame = null }) {
  const [params, setParams] = useSearchParams();
  const inputs = useMemo(() => parseInputs(params), [params]);
  const r = useMemo(() => runModel(inputs), [inputs]);
  const [i, setI] = useState(() => (frame != null ? Math.min(Math.max(0, frame), FRAMES.length - 1) : 0));
  useEffect(() => { if (frame != null) setI(Math.min(Math.max(0, frame), FRAMES.length - 1)); }, [frame]);

  const set = (key, raw) => {
    const next = { ...inputs, [key]: raw === '' ? DEFAULTS[key] : Number(raw) };
    setParams(encodeInputs(next, params), { replace: true });
  };
  const reset = () => setParams(encodeInputs(DEFAULTS, params), { replace: true });
  const dirty = INPUT_SPEC.some((s) => inputs[s.key] !== DEFAULTS[s.key]);
  const y = r.years;
  const yl = y[2];
  const tab = FRAMES[i];

  return (
    <div className="as-screen pf-model">
      <div className="as-tabs" role="tablist">
        {FRAMES.map((t, k) => (
          <button key={t.id} type="button" role="tab" aria-selected={k === i} className={k === i ? 'on' : ''} onClick={() => setI(k)}>{t.label}</button>
        ))}
        <span className="as-tabctl" role="group" aria-label="frame controls">
          <button type="button" onClick={() => setI((i + FRAMES.length - 1) % FRAMES.length)} aria-label="previous frame">‹</button>
          <button type="button" onClick={() => setI((i + 1) % FRAMES.length)} aria-label="next frame">›</button>
          <small>{i + 1} / {FRAMES.length}</small>
        </span>
      </div>

      <div className="pf-grid">
        <aside className="pf-inputs" aria-label="Model inputs">
          <div className="pf-inputs-head">
            <strong>Inputs</strong>
            {dirty && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={reset}>reset</button>}
          </div>
          {GROUPS.map((g) => (
            <div key={g} className="pf-group">
              <p className="pf-group-label">{g}</p>
              {INPUT_SPEC.filter((s) => s.group === g).map((s) => (
                <label key={s.key} className={`pf-input ${inputs[s.key] !== DEFAULTS[s.key] ? 'is-changed' : ''}`}>
                  <span>{s.label}</span>
                  <input type="number" inputMode="numeric" value={inputs[s.key]} min={s.range[0]} max={s.range[1]} step={s.range[2]} onChange={(e) => set(s.key, e.target.value)} />
                  <small>{s.unit}</small>
                </label>
              ))}
            </div>
          ))}
        </aside>

        <div className="pf-pane">
          {tab.id === 'deal' && (
            <>
              <div className="pf-stats">
                <StatCard label="Year-3 revenue" display={usd(yl.revenueTotal)} color={COLORS.teal} />
                <StatCard label="Year-3 gross margin" display={pctText(yl.gmPct)} color={COLORS.amber} />
                <StatCard label="Three-year total" display={usd(r.total3)} />
                <StatCard label="Per company, per month (y3)" display={`$${Math.round(yl.perPortcoMo).toLocaleString()}`} />
              </div>
              <BarChart
                height={180}
                data={y.map((yy) => ({ label: `Year ${yy.year}`, values: [yy.grossMargin, yy.cogsTotal] }))}
                series={[{ name: 'gross margin', color: COLORS.teal }, { name: 'cost of revenue', color: '#8fa3bc' }]}
                formatY={(v) => usd(v)}
              />
              <DataTable
                rowKey={(row) => row.k}
                limit={8}
                columns={[
                  { key: 'k', label: '' },
                  ...y.map((yy) => ({ key: `y${yy.year}`, label: `Year ${yy.year}`, align: 'right' })),
                ]}
                rows={[
                  { k: 'Companies live', ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, `${yy.live} of ${inputs.portcos}`])) },
                  { k: 'Usage maturity', ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, `${USAGE_RAMP[yy.year - 1]}×`])) },
                  { k: 'Commit discount', ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, pctText(yy.discount)])) },
                  { k: 'Per company, per month', ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, `$${Math.round(yy.perPortcoMo).toLocaleString()}`])) },
                  { k: 'Revenue', ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, usd(yy.revenueTotal)])) },
                  { k: 'Cost of revenue', ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, usd(yy.cogsTotal)])) },
                  { k: 'Gross margin', ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, `${usd(yy.grossMargin)} · ${pctText(yy.gmPct)}`])) },
                  { k: 'Recurring run-rate', ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, usd(yy.recurringArr)])) },
                ]}
              />
            </>
          )}

          {tab.id === 'revenue' && (
            <>
              <BarChart
                height={200}
                data={y.map((yy) => ({ label: `Year ${yy.year}`, values: ['platform', 'kit', 'usage', 'services'].map((k) => (k === 'usage' ? yy.revenue.filter((l) => ['seats', 'credits', 'tokens', 'devRuns'].includes(l.key)).reduce((a, l) => a + l.usd, 0) : yy.revenue.find((l) => l.key === k).usd)) }))}
                series={[{ name: 'Platform Enterprise', color: COLORS.teal }, { name: 'App Kit', color: '#8b7cff' }, { name: 'usage', color: '#f5a623' }, { name: 'services', color: '#8fa3bc' }]}
                formatY={(v) => usd(v)}
              />
              <DataTable
                rowKey={(row) => row.key}
                limit={10}
                columns={[
                  { key: 'label', label: 'Line' },
                  { key: 'basis', label: 'Basis', render: (row) => <Basis b={row.basis} /> },
                  ...y.map((yy) => ({ key: `y${yy.year}`, label: `Year ${yy.year}`, align: 'right' })),
                ]}
                rows={[
                  ...yl.revenue.map((l) => ({ key: l.key, label: l.label, basis: l.basis, ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, usd(yy.revenue.find((x) => x.key === l.key).usd)])) })),
                  { key: 'total', label: 'Total', basis: '', ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, usd(yy.revenueTotal)])) },
                ]}
              />
            </>
          )}

          {tab.id === 'margin' && (
            <>
              <div className="pf-stats">
                {y.map((yy) => <StatCard key={yy.year} label={`Year ${yy.year} gross margin`} display={pctText(yy.gmPct)} color={COLORS.teal} />)}
              </div>
              <DataTable
                rowKey={(row) => row.key}
                limit={10}
                columns={[
                  { key: 'label', label: 'Cost of revenue' },
                  { key: 'basis', label: 'Basis', render: (row) => (row.basis ? <Basis b={row.basis} /> : null) },
                  ...y.map((yy) => ({ key: `y${yy.year}`, label: `Year ${yy.year}`, align: 'right' })),
                ]}
                rows={[
                  ...yl.cogs.map((l) => ({ key: l.key, label: l.label, basis: l.basis, ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, usd(yy.cogs.find((x) => x.key === l.key).usd)])) })),
                  { key: 'total', label: 'Total cost of revenue', basis: '', ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, usd(yy.cogsTotal)])) },
                  { key: 'gm', label: 'Gross margin', basis: '', ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, `${usd(yy.grossMargin)} · ${pctText(yy.gmPct)}`])) },
                ]}
              />
            </>
          )}

          {tab.id === 'savings' && (
            <>
              <BarChart
                height={200}
                data={y.map((yy) => ({ label: `Year ${yy.year}`, values: [yy.firmSavings.replaced, yy.firmSavings.avoided] }))}
                series={[{ name: 'software replaced', color: COLORS.teal }, { name: 'builds avoided', color: '#8b7cff' }]}
                formatY={(v) => usd(v)}
              />
              <DataTable
                rowKey={(row) => row.k}
                limit={6}
                columns={[
                  { key: 'k', label: 'The firm’s side' },
                  ...y.map((yy) => ({ key: `y${yy.year}`, label: `Year ${yy.year}`, align: 'right' })),
                ]}
                rows={[
                  { k: `Software replaced (${usd(inputs.replacedSoftware)} per company per year)`, ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, usd(yy.firmSavings.replaced)])) },
                  { k: `Builds avoided (${usd(inputs.avoidedBuild)} per app, new companies)`, ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, usd(yy.firmSavings.avoided)])) },
                  { k: 'Paid to nventr', ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, `− ${usd(yy.firmSavings.spend)}`])) },
                  { k: 'Net to the firm', ...Object.fromEntries(y.map((yy) => [`y${yy.year}`, usd(yy.firmSavings.net)])) },
                ]}
              />
              <p className="pf-note">The pipelines’ own operating savings are not estimated here — they are the firm’s to measure in the pilot.</p>
            </>
          )}
        </div>
      </div>

      <div className="as-foot">
        <span className="as-foot-chip">rate card · costs labelled</span>
        <span>{tab.note}</span>
      </div>
    </div>
  );
}
